import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/lib/auth-context'

const PORTALS = [
  { key: 'sponsor', label: 'Sponsor portal' },
  { key: 'teacher', label: 'Teacher portal' },
  { key: 'prospect', label: 'Prospect / Locker Room portal' },
  { key: 'guardian', label: 'Guardian portal' },
  { key: 'student', label: 'Student portal' },
  { key: 'coach', label: 'Coach portal' },
]

const DEFAULT_SETTINGS = {
  sponsor: 'open',
  teacher: 'open',
  prospect: 'closed',
  guardian: 'closed',
  student: 'closed',
  coach: 'closed',
}

export default function AdminPortalAccess() {
  const { session, isSuperAdmin } = useAuth()
  const bearer = useMemo(
    () => (session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : null),
    [session?.access_token],
  )

  const [settings, setSettings] = useState(DEFAULT_SETTINGS)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isSuperAdmin) return undefined
    let cancelled = false

    const loadSettings = async () => {
      setLoading(true)
      try {
        const response = await fetch('/api/v1/admin/portals/access')
        const payload = await response.json().catch(() => ({}))
        if (!response.ok) throw new Error(payload?.error || 'Failed to load portal settings')

        if (!cancelled && payload?.settings) {
          setSettings((prev) => ({ ...prev, ...payload.settings }))
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('[admin-portal-access] load failed', error)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadSettings()
    return () => {
      cancelled = true
    }
  }, [isSuperAdmin])

  if (!isSuperAdmin) return null

  const handleChange = (key: string, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault?.()
    if (!bearer) return

    setSaving(true)
    try {
      const response = await fetch('/api/v1/admin/portals/access', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...bearer },
        body: JSON.stringify({ settings }),
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(payload?.error || 'Unable to save settings')

      if (payload?.settings) setSettings(payload.settings)
    } catch (error) {
      console.warn('[admin-portal-access] save failed', error)
      alert(error?.message || 'Unable to save settings')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-cocoa">Portal access controls</h3>
          <p className="mt-1 text-xs text-cocoa/70">
            Toggle which portals are accepting new sign-ins. Closed portals are hidden at login.
          </p>
        </div>
        <button
          type="submit"
          disabled={saving || loading}
          className="inline-flex items-center rounded-full bg-cocoa px-4 py-1.5 text-xs font-semibold text-white shadow-sm disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-shell/60 bg-shell/40">
        <table className="min-w-full text-sm">
          <thead className="bg-shell/80 text-xs uppercase tracking-wide text-cocoa/70">
            <tr>
              <th className="px-4 py-2 text-left">Portal</th>
              <th className="px-4 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-shell/70 bg-white">
            {PORTALS.map((portal) => (
              <tr key={portal.key}>
                <td className="px-4 py-3">
                  <div className="font-medium text-cocoa">{portal.label}</div>
                  <div className="text-xs text-cocoa/60">Key: portal.{portal.key}.mode</div>
                </td>
                <td className="px-4 py-3">
                  <select
                    className="w-full rounded-lg border border-shell/80 bg-shell/40 px-3 py-1.5 text-sm"
                    value={settings?.[portal.key as keyof typeof settings] || 'closed'}
                    disabled={loading}
                    onChange={(event) => handleChange(portal.key, event.target.value)}
                  >
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </form>
  )
}
